import React, { FC } from "react"
import { useStaticQuery, graphql } from "gatsby"

import { getImgixUrl, isOnBlogSubdomain, isProduction } from "@utils"

type SEOProps = {
  canonicalUrl?: string | null,
  category?: string | null,
  creator?: string | null,
  description: string | null,
  image: string | null,
  permalink: string | null,
  robots?: string,
  title: string | null,
  type?: string | null,
}

const SEO: FC<SEOProps> = ({
  canonicalUrl,
  category,
  creator,
  description = '',
  image,
  permalink,
  robots = 'index,follow',
  title,
  type
}) => {
  const { site } = useStaticQuery(
    graphql`
      query SEOQuery {
        site {
          siteMetadata {
            author
            description
            siteUrl
            title
            twitter
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description
  const imageUrl = image && (image.includes("//") ? image : getImgixUrl(image))
  const pageUrl = canonicalUrl || `${site.siteMetadata.siteUrl}${permalink}`

  // Append '- Help Scout' to the SEOTitle if it's not already there
  const SEOTitle = title && title.includes(site.siteMetadata.title) ? title : `${title} - ${site.siteMetadata.title}`

  return (
    <>
      <html lang="en" />

      <title>{title}</title>

      {/* Favicon */}
      <link rel="icon" href="/images/favicon/favicon.ico" sizes="32x32" />
      <link rel="icon" href="/images/favicon/favicon.svg" type="image/svg+xml" />
      <link rel="apple-touch-icon" href="/images/favicon/apple-touch-icon.png" />
      <link rel="manifest" href="/manifest.webmanifest" />

      <link rel="canonical" href={pageUrl} />
      <meta name="viewport" content="width=device-width,initial-scale=1.0,maximum-scale=2.0,user-scalable=1" />

      {/* Preload webfonts */}
      <link rel="preload" href="/css/fonts/FKScreamer/FKScreamer-Bold.woff2" as="font" type="font/woff2" crossOrigin="" />
      <link rel="preload" href="/css/fonts/GTAmerica/GT-America-Standard-Regular.woff2" as="font" type="font/woff2" crossOrigin="" />
      <link rel="preload" href="/css/fonts/GTAmerica/GT-America-Standard-Medium.woff2" as="font" type="font/woff2" crossOrigin="" />
      <link rel="preload" href="/css/fonts/Garnett/Garnett-Bold.woff2" as="font" type="font/woff2" crossOrigin="" />
      <link rel="preload" href="/css/fonts/Garnett/Garnett-Semibold.woff2" as="font" type="font/woff2" crossOrigin="" />
      <link rel="preload" href="/css/fonts/Garnett/Garnett-Medium.woff2" as="font" type="font/woff2" crossOrigin="" />
      <link rel="preload" href="/css/fonts/VictorSerif/VictorSerif-40Regular.woff2" as="font" type="font/woff2" crossOrigin="" />
      <link rel="preload" href="/css/fonts/VictorSerif/VictorSerif-45RegularItalic.woff2" as="font" type="font/woff2" crossOrigin="" />

      {/* Preconnect to Google Fonts */}
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link rel="preload" as="style" href="https://fonts.googleapis.com/css2?family=Source+Code+Pro:ital@0;1&family=Geist+Mono:wght@100..900&display=swap" />

      {/* Preconnect to Imgix */}
      <link rel="preconnect" href="https://hs-marketing-contentful.imgix.net" />
      <link rel="preconnect" href="https://hs-marketing.imgix.net" />

      {/* Global meta tags */}
      <meta name="robots" content={isProduction() ? robots : 'noindex,nofollow'} />
      <meta name="description" content={metaDescription} />
      <meta name="author" content={site.siteMetadata.author} />
      <meta name="theme-color" content="#FFFFFF" />
      {imageUrl && <meta name="thumbnail" content={imageUrl} />}
      {category && <meta name="article:tag" content={category} />}

      {/* Facebook */}
      <meta property="og:title" content={SEOTitle} />
      <meta property="og:description" content={metaDescription} />
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      <meta property="og:type" content={type ? type : `website`} />
      <meta property="og:url" content={pageUrl} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={site.siteMetadata.twitter} />
      <meta name="twitter:title" content={SEOTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      {creator && <meta name="twitter:creator" content={`@${creator}`} />}
    </>
  )
}

export default SEO
